/**
 * layout.js — elk layout adapter.
 *
 * computeLayout(elements, layoutName) → Promise<{ [element_id]: { x, y } }>
 *
 * Supported layout names:
 *   'force'       → elk stress (force-directed)
 *   'hierarchical'→ elk layered (Sugiyama)
 *   'circular'    → computed circle (deterministic, no elk needed)
 *   'grid'        → computed square-grid positions (deterministic, no elk needed)
 *   <unknown>     → falls back to 'force'
 *
 * Uses elkjs/lib/elk.bundled.js (worker-less) so it works under jest without
 * any Worker setup.  In production CRA builds the same bundled build is used
 * (the main elkjs entry delegates to a Web Worker only if available).
 *
 * Node sizing: 170 × 48 px pre-measurement fallback — used only to seed the first layout frame
 * before real node sizes are known. Once nodes mount, layout re-runs against the actual measured
 * sizes (sizeById, passed into computeLayout below), so these constants never affect final placement.
 */

// Use the worker-less bundled build so jest can import without a Worker env.
import ELK from 'elkjs/lib/elk.bundled.js';

const elk = new ELK();

export const NODE_WIDTH  = 170;  // pre-measurement fallback width; real layout uses measured sizeById
export const NODE_HEIGHT = 48;   // pre-measurement fallback height; real layout uses measured sizeById
const SPACING = 70;              // min gap between nodes
const DESIRED_EDGE_LENGTH = 140; // target distance between connected nodes (force layout)
const LAYER_SPACING = 80;        // gap between layers (hierarchical layout)

/**
 * Map our layout name to an ELK algorithm id.
 */
function elkAlgorithm(layoutName) {
  switch (layoutName) {
    case 'force':        return 'org.eclipse.elk.stress';
    case 'hierarchical': return 'org.eclipse.elk.layered';
    default:             return 'org.eclipse.elk.stress'; // unknown → force
  }
}

/**
 * Compute grid positions for vertices in a deterministic square-grid arrangement.
 * Vertices are placed in row-major order, left-to-right, top-to-bottom.
 *
 * @param {string[]} vertexIds
 * @returns {{ [element_id]: { x, y } }}
 */
function gridPositions(vertexIds, cellWidth, cellHeight) {
  const cols = Math.max(1, Math.ceil(Math.sqrt(vertexIds.length)));
  const positions = {};
  vertexIds.forEach((id, idx) => {
    const col = idx % cols;
    const row = Math.floor(idx / cols);
    positions[id] = {
      x: col * (cellWidth  + SPACING),
      y: row * (cellHeight + SPACING),
    };
  });
  return positions;
}

/**
 * Compute positions for vertices evenly spaced around a circle (deterministic, no elk).
 * Avoids elk's radial algorithm, which can stack-overflow on cyclic graphs.
 *
 * @param {string[]} vertexIds
 * @returns {{ [element_id]: { x, y } }}
 */
function circlePositions(vertexIds, nodeWidth) {
  const n = vertexIds.length;
  const positions = {};
  if (n === 1) {
    positions[vertexIds[0]] = { x: 0, y: 0 };
    return positions;
  }
  // Radius scales with node count (and the widest node) so neighbours keep a sensible gap around the ring.
  // The 0.7 factor tightens the ring (uniform arc still leaves narrow nodes slack to absorb the wide one).
  const radius = (0.7 * n * (nodeWidth + SPACING)) / (2 * Math.PI);
  vertexIds.forEach((id, idx) => {
    const angle = (2 * Math.PI * idx) / n;
    positions[id] = {
      x: radius * Math.cos(angle),
      y: radius * Math.sin(angle),
    };
  });
  return positions;
}

/**
 * Compute layout positions for a set of elements.
 *
 * @param {Array}  elements   - Element objects ({ element_id, type, ... })
 * @param {string} layoutName - 'force' | 'hierarchical' | 'circular' | 'grid' | <unknown>
 * @returns {Promise<{ [element_id]: { x, y } }>}
 */
export async function computeLayout(elements, layoutName, sizeById) {
  const vertices = elements.filter(el => el.type === 'vertex');
  const edges    = elements.filter(el => el.type === 'edge');

  if (vertices.length === 0) {
    return {};
  }

  // Resolve each vertex's size from the measured sizes when available, else the default. Track the widest /
  // tallest so the deterministic layouts (grid, circular) space cells to fit the largest node.
  const sizeOf = (id) => {
    const s = sizeById && sizeById[id];
    return {
      width: (s && s.width) ? s.width : NODE_WIDTH,
      height: (s && s.height) ? s.height : NODE_HEIGHT,
    };
  };
  let maxWidth = NODE_WIDTH;
  let maxHeight = NODE_HEIGHT;
  for (const v of vertices) {
    const s = sizeOf(v.element_id);
    if (s.width > maxWidth) maxWidth = s.width;
    if (s.height > maxHeight) maxHeight = s.height;
  }

  // Grid and circular are purely computed — no elk call needed.
  if (layoutName === 'grid') {
    return gridPositions(vertices.map(v => v.element_id), maxWidth, maxHeight);
  }
  if (layoutName === 'circular') {
    return circlePositions(vertices.map(v => v.element_id), maxWidth);
  }

  const algorithm = elkAlgorithm(layoutName);

  const layoutOptions = {
    'elk.algorithm': algorithm,
    'elk.spacing.nodeNode': String(SPACING),
  };
  if (algorithm === 'org.eclipse.elk.layered') {
    layoutOptions['elk.direction'] = 'DOWN';
    layoutOptions['elk.layered.spacing.nodeNodeBetweenLayers'] = String(LAYER_SPACING);
  } else if (algorithm === 'org.eclipse.elk.stress') {
    layoutOptions['elk.stress.desiredEdgeLength'] = String(DESIRED_EDGE_LENGTH);
  }

  const graph = {
    id: 'root',
    layoutOptions,
    children: vertices.map(v => {
      const s = sizeOf(v.element_id);
      return { id: v.element_id, width: s.width, height: s.height };
    }),
    edges: edges.map(e => ({
      id: e.element_id,
      sources: [e.from_id],
      targets: [e.to_id],
    })),
  };

  const result = await elk.layout(graph);

  const positions = {};
  for (const node of (result.children || [])) {
    positions[node.id] = { x: node.x || 0, y: node.y || 0 };
  }
  return positions;
}
